import React, { useEffect, useState } from "react";
import {
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Button,
  Select,
  Spin,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import includes from "lodash/includes";
import { useDispatch } from "react-redux";

import PartSetting from "../partSettings";
import { fetchProductionComponents } from "../../../redux/actions/productionCompActions";
import MinMaxTimePicker, { MinMaxEndTimePicker } from "../ProductionTimePicker";
import ProductionComponentSelect from "../productionComponentSelect";
import { uniqBy } from "lodash";
import { calculateExtrutionProductionCount } from "../../../utilities/shift";

const ProductionCompExtrutionSetting = ({
  form,
  filter,
  minTime,
  maxTime,
  initialValues,
  config
}) => {
  const [loading, setLoading] = useState(false);
  const [pcParts, setPcParts] = useState({});
  const [productionComArr, setProductionCompArr] = useState([]);
  const [currentProductionComp, setCurrentProductionComp] = useState({});
  const dispatch = useDispatch();
  const getProductionComponents = (uuid, query) => {
    setLoading(true);

    dispatch(fetchProductionComponents(uuid, query))
      .then((res) => {
        setProductionCompArr(res);
      })
      .catch((error) => {
        console.error("Failed to fetch production components:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function plannedProductionCountHandler(name, newValue) {
    const allFormValues = newValue || form.getFieldValue(["components", name]);
    const machine_speed = get(allFormValues, "machine_speed");
    const start_time = get(allFormValues, "start_time");
    const end_time = get(allFormValues, "end_time");
    const planned_down_time = get(allFormValues, "planned_down_time");
    const test_length = get(allFormValues, "test_length");
    if (
      get(allFormValues, "machine_speed") &&
      get(allFormValues, "start_time") &&
      get(allFormValues, "end_time") &&
      get(allFormValues, "parts_full")
    ) {
      get(allFormValues, "parts_full", []).forEach((part, rowIndex) => {
        const plannedProduction = calculateExtrutionProductionCount(
          start_time,
          end_time,
          planned_down_time || 0,
          machine_speed,
          test_length
        );

        form.setFieldValue(
          [
            "components",
            name,
            "parts_full",
            rowIndex,
            "meta",
            "planned_length",
          ],
          plannedProduction
        );
      });
    }
  }
  useEffect(() => {
    const query = { type: get(filter, "templateType") };
    getProductionComponents(get(filter, "company.id"), query);
  }, []);
  return (
    <Spin spinning={loading}>
      <Divider orientation="left">Production Component Settings</Divider>
      <Form.List name="components">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name }) => {
              return (
                <div
                  key={key}
                  style={{
                    marginBottom: 24,
                    borderBottom: "1px dashed #ccc",
                    paddingBottom: 16,
                  }}
                >
                  <Row gutter={16}>
                    <ProductionComponentSelect
                      form={form}
                      name={name}
                      productionComArr={productionComArr}
                      setCurrentProductionComp={setCurrentProductionComp}
                      setPcParts={setPcParts}
                      filter={filter}
                    />
                    <Col md={6} xs={24}>
                      <Form.Item
                        label="Parts"
                        name={[name, "parts"]}
                        rules={[
                          {
                            required: true,
                            message: "Please Select Part ...!",
                          },
                        ]}
                      >
                        <Select
                          options={map(
                            get(currentProductionComp, `${[name]}.parts`),
                            (part) => ({
                              label: get(part, "name"),
                              value: get(part, "id"),
                            })
                          )}
                          size="small"
                          placeholder="Select Part"
                          showSearch
                          optionFilterProp="label"
                          mode={
                            get(currentProductionComp, `[${name}].is_default`)
                              ? undefined
                              : "multiple"
                          }
                          className="w-full"
                          onChange={(value) => {
                            const partsFullFiltered = get(
                              currentProductionComp,
                              `${[name]}.parts`
                            ).filter((part) =>
                              includes(value, get(part, "id"))
                            );                           
                            form.setFieldValue(
                              ["components", name, "parts_full"],
                              uniqBy(
                                [
                                  ...get(
                                    initialValues,
                                    `components[${name}].parts_full`,
                                    pcParts[name]
                                  ),
                                  ...partsFullFiltered,
                                ].filter((part) =>
                                  includes(value, get(part, "id"))
                                ),
                                "id"
                              )
                            );
                            const updatedValues = form.getFieldValue([
                              "components",
                              name,
                            ]);
                            updatedValues.parts = value;
                            updatedValues.parts_full = uniqBy(
                              [
                                ...get(
                                  initialValues,
                                  `components[${name}].parts_full`,
                                  pcParts[name]
                                ),
                                ...partsFullFiltered,
                              ].filter((part) =>
                                includes(value, get(part, "id"))
                              ),
                              "id"
                            );
                            plannedProductionCountHandler(name, updatedValues);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={6} xs={24}>
                      <MinMaxTimePicker
                        name={name}
                        minTime={minTime}
                        maxTime={maxTime}
                        minuteStep={15}
                        form={form}
                        onChange={plannedProductionCountHandler}
                      />
                    </Col>
                    <Col md={6} xs={24}>
                      <MinMaxEndTimePicker
                        name={name}
                        form={form}
                        className="w-full"
                        size="small"
                        filter={filter}
                        minTime={minTime}
                        maxTime={maxTime}
                        minuteStep={15}
                        onChange={plannedProductionCountHandler}
                      />
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item name={[name, "test_length"]} label="Test length">
                        <InputNumber
                          placeholder="Test Length"
                          size="small"
                          addonAfter="meter"
                          className="w-full"
                          defaultValue={0}
                          onChange={(newValue) => {
                            const updatedValues = form.getFieldValue([
                              "components",
                              name,
                            ]);
                            updatedValues.test_length = newValue;
                            plannedProductionCountHandler(name, updatedValues);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "machine_speed"]}
                        label="Machine speed"
                        rules={[
                          { required: true, message: "Machine speed is required" },
                        ]}
                      >
                        <InputNumber
                          placeholder="Machine speed"
                          addonAfter="meter/min"
                          size="small"
                          className="w-full"
                          onChange={(newValue) => {
                            const updatedValues = form.getFieldValue([
                              "components",
                              name,
                            ]);
                            updatedValues.machine_speed = newValue;
                            plannedProductionCountHandler(name, updatedValues);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={24}>
                      <Form.Item
                        name={[name, "planned_down_time"]}
                        label="Planned Down Time"
                        rules={[
                          {
                            required: true,
                            message: "Planned Down Time is required",
                          },
                        ]}
                        min={0}
                        defaultValue={0}
                      >
                        <InputNumber
                          placeholder="Planned Down Time"
                          addonAfter="min"
                          size="small"
                          className="w-full"
                          defaultValue={0}
                          onChange={(newValue) => {
                            form.setFieldValue(
                              ["components", name, "planned_down_time"],
                              newValue
                            );
                            const updatedValues = form.getFieldValue([
                              "components",
                              name,
                            ]);
                            updatedValues.planned_down_time = newValue;
                            plannedProductionCountHandler(name, updatedValues);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={6} xs={24}>
                      <Form.Item name={[name, "job_id"]} label="Job ID">
                        <Input
                          placeholder="Job ID"
                          className="w-full"
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col md={6} xs={24}>
                      <Form.Item
                        name={[name, "batch_number"]}
                        label="Batch Number"
                      >
                        <Input
                          placeholder="Batch Number"
                          className="w-full"
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item noStyle shouldUpdate>
                    {() => (
                      <PartSetting
                        name={name}
                        form={form}
                        filter={filter}
                        partsData={
                          form.getFieldValue([
                            "components",
                            name,
                            "parts_full",
                          ]) || []
                        }
                        config={config}
                      />
                    )}
                  </Form.Item>
                  {get(fields, "length") > 1 && (
                    <Col span={24} className="mt-4">
                      <Button
                        type="default"
                        size="small"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove
                      </Button>
                    </Col>
                  )}
                </div>
              );
            })}
            <Col className="mx-auto max-w-[500px]">
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() =>
                    add({
                      test_shots: get(
                        initialValues,
                        "components.0.test_length",
                        0
                      ),
                      machine_speed: get(
                        initialValues,
                        "components.0.machine_speed",
                        0
                      ),
                      planned_down_time: get(
                        initialValues,
                        "components.0.planned_down_time",
                        0
                      ),
                    })
                  }
                  block
                  size="small"
                  className="w-auto"
                  icon={<PlusOutlined />}
                >
                  Add Production Component
                </Button>
              </Form.Item>
            </Col>
          </>
        )}
      </Form.List>
    </Spin>
  );
};

export default ProductionCompExtrutionSetting;
